import { Injectable } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  BenchmarkDataPrimaryLookupService,
  CcyLookupService,
  CcyPromiseLookupService,
  HedgeOrderDataPrimaryLookupService,
  ProfilePromiseActionService,
  Roles,
  SystemConfigPromiseService,
  TraderGroupLookupService,
  TraderGroupPromiseLookupService,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ActivatedRoute } from "@angular/router";
import { BmarkBaseRiskDataService } from "app/bfx-bmark-summary/blotters/spot-risk/services/bmark-base-risk-data.service";
import { BmarkFixingsManagerService } from "app/bfx-bmark-summary/services/bmark-fixings-manager.service";
import { BenchmarkPositionLookupService } from "shared/cubiq-services/lookup/model/impl/BenchmarkPositionLookupService";
import {
  FwdRiskModel,
  FwdRiskPair,
} from "shared/cubiq-services/lookup/model/impl/bmrisk/FwdRiskModel";
import { SpotRiskPair } from "shared/cubiq-services/lookup/model/impl/bmrisk/SpotRiskModel";
import { BLOTTER_RELOAD_TYPE } from "shared/cubiq-services/lookup/model/impl/bmrisk/BaseBMRiskModel";
import { BmarkFwdRiskTableDefinitionFactory } from "shared/cubiq-services/model/BmarkFwdRiskTableDefinitionFactory";
import { BmarkRiskDisplayModel } from "shared/cubiq-services/model/benchmarks/BmarkRiskDisplay.model";
import { BmarkFwdRiskDisplayModel } from "shared/cubiq-services/model/benchmarks/BmarkSpotRiskDisplay.model";
import { BmarkClientOrdersDataService } from "../../client-orders/services/bmark-client-orders-data.service";

/**
 * Service providing aggregate risk data model to benchmark forward risk blotter
 */
@Injectable()
export class BmarkFwdRiskDataService extends BmarkBaseRiskDataService {
  public fwdRiskModel: FwdRiskModel;

  constructor(
    dataService: BenchmarkDataPrimaryLookupService,
    hedgingDataService: HedgeOrderDataPrimaryLookupService,
    tableDefinitionService: BmarkFwdRiskTableDefinitionFactory,
    bmarkManager: BmarkFixingsManagerService,
    profileActionService: ProfilePromiseActionService,
    clientOrdersDataService: BmarkClientOrdersDataService,
    traderGroupLookup: TraderGroupPromiseLookupService,
    ccyService: CcyLookupService,
    _traderGroupLookup: TraderGroupLookupService,
    _ccyLookupService: CcyPromiseLookupService,
    cmdLine: ActivatedRoute,
    positionService: BenchmarkPositionLookupService,
    sysConfigLookup: SystemConfigPromiseService,
  ) {
    super(
      dataService,
      hedgingDataService,
      tableDefinitionService,
      bmarkManager,
      profileActionService,
      clientOrdersDataService,
      traderGroupLookup,
      ccyService,
      _traderGroupLookup,
      _ccyLookupService,
      cmdLine,
      positionService,
      sysConfigLookup,
    );
  }

  /**
   * Called during constructor so initialisers at this level will not yet be called
   */
  protected override initialize() {
    // Ensure no subscriptons made user not authorized
    const userEnabled = this.sysConfigLookup.hasRole(Roles.ROLE_VIEW_BENCHMARK_SUMMARY_FWD);

    // Fwd Risk Model
    this.fwdRiskModel = new FwdRiskModel(
      this.clientOrdersDataService,
      this.baseTraderGroupLookup,
      this.ccyService,
      userEnabled,
      this.cmdLine,
    );

    userEnabled.then((isEnabled) => {
      if (isEnabled) {
        // Only want to maintain the data model if the user is authorised to see it
        this.fwdRiskModel.subscribe(this);

        this.dataService.isPrimed().then((primed: boolean) => {
          this.createListOfCcyMaterials();
          // Fixing Manager
          this.bmarkManager.displayFixing$.subscribe(this.onFixingManagerEvent.bind(this));
          // Fixing Lookup
          //this.dataService.getBmarkOrderDataUpdateSubject().subscribe(this.onBmarkOrderDataEvent.bind(this));
          this.dataService.fixingModelUpdateSubject.subscribe(this.onFixingModelUpdate.bind(this));
          this.positionService.changePositionSubject.subscribe(
            this.onPositionModeChange.bind(this),
          );
        });
      }
    });
  }

  /* Event handlers */

  protected override tableDataReload(
    reason?: BLOTTER_RELOAD_TYPE,
    forceRepaint: boolean = false,
  ): void {
    logger.out.info(
      `bmfwdrd-s: tableDataReload() -> RELOAD | Data: reason= ${BLOTTER_RELOAD_TYPE[reason]}`,
    );
    this._rawData = Object.create(null);

    this.blotterChangeSubject.next(reason);
    if (this.fwdRiskModel.fwdRiskData instanceof Map && this.fwdRiskModel.fwdRiskData.size > 0) {
      let data = this.processRiskData(this.fwdRiskModel.fwdRiskData as any);
      super.onFullUpdateEvent(data, forceRepaint);
    } else {
      super.onFullUpdateEvent([], forceRepaint);
    }
  }

  protected override tableDataUpdateRows(
    ccyPairIds: Array<string>,
    forceRepaint: boolean = false,
  ): void {
    if (Array.isArray(ccyPairIds) && ccyPairIds.length > 0) {
      // delete the rows which are going to be updated
      this.tableDataDeleteRows(ccyPairIds);
      //
      // get the data
      let ccyPairsUpdateData: Map<string, FwdRiskPair> = new Map<string, FwdRiskPair>();
      for (let ccyPair of ccyPairIds) {
        if (this.fwdRiskModel.fwdRiskData.has(ccyPair)) {
          ccyPairsUpdateData.set(ccyPair, this.fwdRiskModel.fwdRiskData.get(ccyPair));
        }
      }
      // do the update (add the rows)
      if (ccyPairsUpdateData.size > 0) {
        this.onFullUpdateEvent(this.processRiskData(ccyPairsUpdateData), forceRepaint);
      }
    }
  }

  protected override getAlertThresholds(fixingId: string): Map<string, CubiqTS.BigNumber> {
    return this.dataService.getFwdAlertThresholds(fixingId);
  }
  protected override getWarningThresholds(fixingId: string): Map<string, CubiqTS.BigNumber> {
    return this.dataService.getFwdWarningThresholds(fixingId);
  }
  protected override createDisplayModel(key: string, value: any): BmarkRiskDisplayModel {
    if (value instanceof FwdRiskPair) {
      return new BmarkFwdRiskDisplayModel(this.activeFixingId + key, value);
    }
    return null;
  }
}
